import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { LanguageSelector } from "@/components/site/LanguageSelector";
import { CONTACT } from "@/data";

const NAV_KEYS = ["about", "services", "why", "portfolio", "process", "contact"];

export const Header = () => {
  const { t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 ${
        scrolled ? "glass" : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="container-x flex items-center justify-between h-16 sm:h-20">
        <button
          data-testid="brand-logo"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="flex items-center gap-2.5 group"
        >
          <span className="grid place-items-center h-9 w-9 rounded-lg bg-[#E85D04] text-white font-display font-extrabold text-lg">
            М
          </span>
          <span className="font-display font-extrabold text-[15px] sm:text-lg leading-none tracking-tight">
            Мастер на все руки
          </span>
        </button>

        <nav className="hidden lg:flex items-center gap-8">
          {NAV_KEYS.map((k) => (
            <button
              key={k}
              data-testid={`nav-${k}`}
              onClick={() => go(k)}
              className="text-sm font-semibold text-[#C7CCD6] hover:text-[#FF8C42] transition-colors relative after:absolute after:-bottom-1.5 after:left-0 after:h-0.5 after:w-0 after:bg-[#E85D04] hover:after:w-full after:transition-all after:duration-300"
            >
              {t.nav[k]}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-2.5">
          <LanguageSelector />
          <button
            data-testid="header-quote-btn"
            onClick={() => go("contact")}
            className="hidden sm:inline-flex items-center gap-2 rounded-full bg-[#E85D04] hover:bg-[#D05303] px-5 py-2.5 text-sm font-bold text-white transition-colors"
          >
            <Phone className="h-4 w-4" />
            {t.header.quote}
          </button>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden grid place-items-center h-10 w-10 rounded-full border border-white/15 text-[#FAFAFA]"
            aria-label="Menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="lg:hidden overflow-hidden glass border-t border-white/10"
          >
            <div className="container-x py-6 flex flex-col gap-1">
              {NAV_KEYS.map((k) => (
                <button
                  key={k}
                  data-testid={`mobile-nav-${k}`}
                  onClick={() => go(k)}
                  className="text-left py-3 text-base font-semibold text-[#C7CCD6] hover:text-[#FF8C42] border-b border-white/5"
                >
                  {t.nav[k]}
                </button>
              ))}
              <a
                data-testid="mobile-call-link"
                href={`tel:${CONTACT.phones[0].tel}`}
                className="mt-4 inline-flex justify-center items-center gap-2 rounded-full bg-[#E85D04] px-5 py-3 font-bold text-white"
              >
                <Phone className="h-4 w-4" /> {CONTACT.phones[0].display}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
